import { useState } from 'react'
import {
  useUnbindCustomAgentWritebackToolMutation,
  type CustomAgentBoundWritebackTool,
  type CustomAgentWritebackToolDefinition
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import { displayDbConnectionName } from '../../../lib/display-db-connection-name'
import { ConfirmDialog } from '../components/DeleteConfirmDialog'
import { formatDate } from '../util/format'
import { proxyApiErrorMessage } from './config-detail-chrome'

export function parseWritebackToolDefinition(
  raw: unknown
): CustomAgentWritebackToolDefinition | null {
  if (raw == null) return null
  let obj: unknown = raw
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw) as unknown
    } catch {
      return null
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null
  const rec = obj as Record<string, unknown>
  const query = typeof rec.parametrized_query === 'string' ? rec.parametrized_query : ''
  if (!query) return null
  const params = Array.isArray(rec.params)
    ? rec.params.filter((entry): entry is string => typeof entry === 'string')
    : []
  const connectionId = typeof rec.connection_id === 'string' ? rec.connection_id : ''
  return { parametrized_query: query, params, connection_id: connectionId }
}

interface WritebackToolsSectionProps {
  configId: string
  tools: CustomAgentBoundWritebackTool[]
  onError: (message: string) => void
}

/**
 * Bound writeback tools on a custom-agent detail page. Unbind is immediate
 * (no approval card); last unbind prunes the org registry row.
 */
export function WritebackToolsSection({ configId, tools, onError }: WritebackToolsSectionProps) {
  const [unbindTarget, setUnbindTarget] = useState<CustomAgentBoundWritebackTool | null>(null)
  const [unbind, { isLoading: unbinding }] = useUnbindCustomAgentWritebackToolMutation()

  const doUnbind = async () => {
    if (!unbindTarget) return
    try {
      await unbind({ configId, toolId: unbindTarget.tool_id }).unwrap()
      setUnbindTarget(null)
    } catch (e) {
      onError(proxyApiErrorMessage(e, 'Failed to unbind writeback tool.'))
      setUnbindTarget(null)
    }
  }

  return (
    <>
      {tools.length === 0 ? (
        <p className="m-0 rounded-xl border border-dashed border-line bg-card px-4 py-3 text-sm text-ink-3">
          No writeback tools bound.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {tools.map((tool) => {
            const def = parseWritebackToolDefinition(tool.definition)
            return (
              <li
                key={tool.tool_id}
                className="flex flex-col gap-2 rounded-xl border border-line bg-card px-4 py-3 text-sm text-ink-2 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-ink">
                      <span className="font-mono">db_write_{tool.name}</span>
                      <span className="ml-2 text-xs font-normal uppercase tracking-wide text-ink-4">
                        {tool.kind}
                      </span>
                    </div>
                    {(tool.bound_reason || tool.reason) && (
                      <p className="m-0 mt-1 text-xs text-ink-3">
                        {tool.bound_reason || tool.reason}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUnbindTarget(tool)}
                    disabled={unbinding}
                  >
                    Unbind
                  </Button>
                </div>
                {def ? (
                  <div className="flex flex-col gap-1 text-xs text-ink-3">
                    <div>
                      <span className="text-ink-4">Database:</span>{' '}
                      <span className="font-mono" title={def.connection_id || undefined}>
                        {displayDbConnectionName(def.connection_id)}
                      </span>
                    </div>
                    <div>
                      <span className="text-ink-4">Params:</span>{' '}
                      {def.params.length > 0 ? def.params.map((p) => `:${p}`).join(', ') : 'none'}
                    </div>
                    <pre className="m-0 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded-md border border-line bg-muted/40 px-2 py-1.5 font-mono text-11 leading-relaxed text-ink-2">
                      {def.parametrized_query}
                    </pre>
                    <div className="text-ink-4">Bound {formatDate(tool.bound_at)}</div>
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}

      <ConfirmDialog
        open={unbindTarget !== null}
        onOpenChange={(open) => {
          if (!open) setUnbindTarget(null)
        }}
        title={unbindTarget ? `Unbind db_write_${unbindTarget.name}?` : 'Unbind writeback tool?'}
        description="If this is the last agent using the tool, the org registry row is removed. In-flight runs keep their snapshot."
        confirmLabel="Unbind"
        destructive
        onConfirm={() => {
          void doUnbind()
        }}
        busy={unbinding}
      />
    </>
  )
}
